import { ServiceAction, ServicePartActionUtils } from "@repo/common"
import {
  BatteryCharging,
  Circle,
  CircleCheck,
  Disc,
  Droplet,
  Flame,
  LifeBuoy,
  LucideIcon,
  Mountain,
  Plus,
  RefreshCw,
  Repeat,
  Search,
  Settings,
  Sparkles,
  Stethoscope,
  Syringe,
  Triangle,
  Wrench,
  X,
} from "lucide-react"
import { IconType } from "react-icons"
import { BiSolidCog } from "react-icons/bi"
import { FaPumpSoap } from "react-icons/fa"
import { FaOilCan } from "react-icons/fa6"
import { GiIceCube, GiMagicBroom, GiScrewdriver, GiSkis } from "react-icons/gi"

const SERVICE_ACTION_ICONS: Record<ServiceAction, LucideIcon | IconType> = {
  [ServiceAction.CHANGE]: Repeat,
  [ServiceAction.ADJUST]: Wrench,
  [ServiceAction.TROUBLESHOOT]: Search,
  [ServiceAction.DO_NOTHING]: X,
  [ServiceAction.UPDATE]: RefreshCw,
  [ServiceAction.BLEED]: Droplet,
  [ServiceAction.TRUE]: CircleCheck,
  [ServiceAction.MOUNT_TUBELESS]: Syringe,
  [ServiceAction.MOUNT_TUBE]: Circle,
  [ServiceAction.NEXUS_DIP]: FaOilCan,
  [ServiceAction.MOUNT]: Plus,
  [ServiceAction.BASIC_SERVICE]: Settings,
  [ServiceAction.FULL_SERVICE]: BiSolidCog,
  [ServiceAction.CLEAN]: GiMagicBroom,
  [ServiceAction.LUBRICATE]: FaPumpSoap,
  [ServiceAction.WAX]: GiSkis,
  [ServiceAction.ACTIVE_WAX]: Flame,
  [ServiceAction.STONE_GRIND]: Mountain,
  [ServiceAction.BASE_REPAIR]: Sparkles,
  [ServiceAction.SCRAPE]: GiScrewdriver,
  [ServiceAction.EDGE_SHARPEN]: Triangle,
  [ServiceAction.EDGE_DETUNE]: Disc,
  [ServiceAction.BLADE_SHARPEN]: GiIceCube,
  [ServiceAction.CHECK]: Stethoscope,
  [ServiceAction.CHANGE_WHEEL_BEARINGS]: LifeBuoy,
  [ServiceAction.CHANGE_BATTERIES]: BatteryCharging,
}

// Translation keys rather than text, so callers render them with `t()` in the
// viewer's language.
export const serviceActionMapper = (serviceAction: ServiceAction) => ({
  icon: SERVICE_ACTION_ICONS[serviceAction],
  labelKey: ServicePartActionUtils.toLabelKey(serviceAction),
  nounKey: ServicePartActionUtils.toNounKey(serviceAction),
  pastPassiveKey: ServicePartActionUtils.toPastPassiveKey(serviceAction),
})

export const serviceActions = [
  { ...serviceActionMapper(ServiceAction.CHANGE), type: ServiceAction.CHANGE },
  { ...serviceActionMapper(ServiceAction.ADJUST), type: ServiceAction.ADJUST },
  { ...serviceActionMapper(ServiceAction.CLEAN), type: ServiceAction.CLEAN },
  {
    ...serviceActionMapper(ServiceAction.LUBRICATE),
    type: ServiceAction.LUBRICATE,
  },
  { ...serviceActionMapper(ServiceAction.UPDATE), type: ServiceAction.UPDATE },
  { ...serviceActionMapper(ServiceAction.TRUE), type: ServiceAction.TRUE },
  { ...serviceActionMapper(ServiceAction.BLEED), type: ServiceAction.BLEED },
  {
    ...serviceActionMapper(ServiceAction.MOUNT_TUBELESS),
    type: ServiceAction.MOUNT_TUBELESS,
  },
  {
    ...serviceActionMapper(ServiceAction.MOUNT_TUBE),
    type: ServiceAction.MOUNT_TUBE,
  },
  {
    ...serviceActionMapper(ServiceAction.NEXUS_DIP),
    type: ServiceAction.NEXUS_DIP,
  },
  {
    ...serviceActionMapper(ServiceAction.MOUNT),
    type: ServiceAction.MOUNT,
  },
  {
    ...serviceActionMapper(ServiceAction.BASIC_SERVICE),
    type: ServiceAction.BASIC_SERVICE,
  },
  {
    ...serviceActionMapper(ServiceAction.FULL_SERVICE),
    type: ServiceAction.FULL_SERVICE,
  },
  {
    ...serviceActionMapper(ServiceAction.TROUBLESHOOT),
    type: ServiceAction.TROUBLESHOOT,
  },
  {
    ...serviceActionMapper(ServiceAction.CHECK),
    type: ServiceAction.CHECK,
  },
  {
    ...serviceActionMapper(ServiceAction.CHANGE_WHEEL_BEARINGS),
    type: ServiceAction.CHANGE_WHEEL_BEARINGS,
  },
  {
    ...serviceActionMapper(ServiceAction.CHANGE_BATTERIES),
    type: ServiceAction.CHANGE_BATTERIES,
  },
  // Winter sports actions
  { ...serviceActionMapper(ServiceAction.WAX), type: ServiceAction.WAX },
  {
    ...serviceActionMapper(ServiceAction.ACTIVE_WAX),
    type: ServiceAction.ACTIVE_WAX,
  },
  {
    ...serviceActionMapper(ServiceAction.STONE_GRIND),
    type: ServiceAction.STONE_GRIND,
  },
  {
    ...serviceActionMapper(ServiceAction.BASE_REPAIR),
    type: ServiceAction.BASE_REPAIR,
  },
  { ...serviceActionMapper(ServiceAction.SCRAPE), type: ServiceAction.SCRAPE },
  {
    ...serviceActionMapper(ServiceAction.EDGE_SHARPEN),
    type: ServiceAction.EDGE_SHARPEN,
  },
  {
    ...serviceActionMapper(ServiceAction.EDGE_DETUNE),
    type: ServiceAction.EDGE_DETUNE,
  },
  {
    ...serviceActionMapper(ServiceAction.BLADE_SHARPEN),
    type: ServiceAction.BLADE_SHARPEN,
  },
  // Always last
  {
    ...serviceActionMapper(ServiceAction.DO_NOTHING),
    type: ServiceAction.DO_NOTHING,
  },
] as const
